import { ApiError, type IdempotencyKey } from "@baton/api";

/**
 * `contracts/client-api.md` § The write queue, `research.md` W-24: in-memory only, bounded
 * at 50 entries and 10 minutes, draining in order on reconnect with each entry's original
 * key and `If-Match`, a 412 raising the same conflict notice as an online conflict, the active
 * queries refreshed afterwards, and an over-bound entry dropped with a toast naming the item and the
 * field.
 *
 * 2026-09-21: a drain that sent nothing refreshes nothing. The refresh is there because the writes
 * that just landed have moved the server on from what the cache holds; with no writes to land there
 * is nothing for it to reconcile, and running it anyway made every connection blip re-read the whole
 * screen. What the refresh itself does with a paged list is `recovery.ts`.
 */
export const QUEUE_MAX_ENTRIES = 50;
export const QUEUE_MAX_AGE_MS = 10 * 60_000;

export interface QueuedWrite {
  readonly idempotencyKey: IdempotencyKey;
  readonly entityId: string;
  readonly itemLabel: string;
  readonly fields: readonly string[];
  readonly version: number | null;
  readonly queuedAt: number;
  /** Send the write with its original key and version; the argument is unused when the entry has no version. */
  readonly send: () => Promise<unknown>;
  /** A 412 during the drain raises the same reconciliation as an online conflict. */
  readonly onConflict: (error: ApiError) => void;
}

export interface DroppedWrite {
  readonly itemLabel: string;
  readonly fields: readonly string[];
  readonly reason: "over-size" | "over-age";
}

export interface WriteQueue {
  readonly size: number;
  enqueue(write: Omit<QueuedWrite, "queuedAt">, now?: number): DroppedWrite[];
  /** Send every entry in order; returns what was dropped as stale before sending. */
  drain(now?: number): Promise<DroppedWrite[]>;
  /** Drop every entry without sending, on sign-out everywhere and on a restore discard. */
  clear(): void;
  readonly subscribe: (listener: () => void) => () => void;
}

export interface WriteQueueDependencies {
  readonly onDropped: (dropped: DroppedWrite) => void;
  /** Read the active queries again once the drained writes have landed; not called by an empty drain. */
  readonly invalidateActive: () => Promise<void>;
  readonly clock?: () => number;
}

export function createWriteQueue(deps: WriteQueueDependencies): WriteQueue {
  const clock = deps.clock ?? Date.now;
  let entries: QueuedWrite[] = [];
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  const expire = (now: number): DroppedWrite[] => {
    const dropped: DroppedWrite[] = [];
    entries = entries.filter((entry) => {
      if (now - entry.queuedAt > QUEUE_MAX_AGE_MS) {
        dropped.push({ itemLabel: entry.itemLabel, fields: entry.fields, reason: "over-age" });
        return false;
      }
      return true;
    });
    for (const d of dropped) deps.onDropped(d);
    return dropped;
  };
  return {
    get size() {
      return entries.length;
    },
    enqueue(write, now = clock()) {
      const dropped = expire(now);
      entries.push({ ...write, queuedAt: now });
      while (entries.length > QUEUE_MAX_ENTRIES) {
        const oldest = entries.shift();
        if (oldest) {
          const d = { itemLabel: oldest.itemLabel, fields: oldest.fields, reason: "over-size" as const };
          dropped.push(d);
          deps.onDropped(d);
        }
      }
      notify();
      return dropped;
    },
    async drain(now = clock()) {
      const dropped = expire(now);
      const batch = entries;
      entries = [];
      notify();
      for (const entry of batch) {
        try {
          await entry.send();
        } catch (error) {
          if (error instanceof ApiError && error.status === 412) entry.onConflict(error);
          else if (error instanceof ApiError && error.isTransport) {
            // The connection went again mid-drain: keep this and the rest for the next drain.
            entries = [entry, ...batch.slice(batch.indexOf(entry) + 1), ...entries];
            notify();
            break;
          }
        }
      }
      if (batch.length > 0) await deps.invalidateActive();
      return dropped;
    },
    clear() {
      entries = [];
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}
